import markup from './social-media-icons.component.html?raw';
import styles from './social-media-icons.component.scss?inline';
import iconSpriteUrl from './social-icons.svg?url';

type SocialPlatform = 'linkedin' | 'facebook' | 'youtube' | 'instagram' | 'twitter';

const PLATFORM_ID: Record<SocialPlatform, string> = {
  linkedin: 'l',
  facebook: 'f',
  youtube: 'y',
  instagram: 'i',
  twitter: 't',
};

const template = document.createElement('template');
template.innerHTML = `
  <style>${styles}</style>
  ${markup}
`;

// Vite may emit a root-relative URL in dev (e.g. /theme/src/...svg). Resolve it
// against the module URL so the browser requests from the Vite origin, not WP.
const resolvedIconSpriteUrl = new URL(iconSpriteUrl, import.meta.url).href;

class SocialMediaIconsElement extends HTMLElement {
  private readonly root: ShadowRoot;

  public constructor() {
    super();

    this.root = this.attachShadow({ mode: 'open' });
    this.root.appendChild(template.content.cloneNode(true));
  }

  static get observedAttributes(): string[] {
    return [
      'size',
      'variant',
      'linkedin-url',
      'facebook-url',
      'youtube-url',
      'instagram-url',
      'twitter-url',
    ];
  }

  public connectedCallback(): void {
    this.applyLinks();
    this.applyIconSources();
  }

  public attributeChangedCallback(name: string, _oldValue: string | null, _newValue: string | null): void {
    void _oldValue;
    void _newValue;

    if (name === 'variant') {
      this.applyIconSources();
      return;
    }

    this.applyLinks();
  }

  private applyLinks(): void {
    const platforms: SocialPlatform[] = ['linkedin', 'facebook', 'youtube', 'instagram', 'twitter'];

    for (const platform of platforms) {
      const listItem = this.root.querySelector<HTMLElement>(`li[data-platform="${platform}"]`);
      const anchor = this.root.querySelector<HTMLAnchorElement>(`a[data-link="${platform}"]`);
      const url = this.getAttribute(`${platform}-url`)?.trim() ?? '';

      if (!listItem || !anchor) {
        continue;
      }

      if (!url) {
        anchor.removeAttribute('href');
      } else {
        anchor.setAttribute('href', url);
      }
    }
  }

  private applyIconSources(): void {
    const iconColor = this.getAttribute('variant') === 'dark' ? '3d3935' : 'ffffff';

    const iconElements = this.root.querySelectorAll<HTMLImageElement>('img[data-icon]');
    for (const iconElement of iconElements) {
      const platform = iconElement.getAttribute('data-icon') as SocialPlatform | null;
      if (!platform) {
        continue;
      }

      const platformId = PLATFORM_ID[platform];
      iconElement.src = `${resolvedIconSpriteUrl}#${platformId}-${iconColor}`;
    }
  }
}

if (!customElements.get('social-media-icons')) {
  customElements.define('social-media-icons', SocialMediaIconsElement);
}
